// HOME (/) — 히어로 슬라이드 · 파트너 네트워크 · WHAT WE DO · SELECTED WORK
// 디자인 시안 HTML을 그대로 옮긴 화면입니다. 문구·사진·구성은 시안과 같게 유지해 주세요.
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePageMotion } from "../motion/usePageMotion";
import { reducedMotion } from "../motion/smoothScroll";

const SLIDES = [
  {
    image: "/assets/img/pages/home-hero-1-concept.jpg",
    lines: ["KOREA TO THAILAND.", "WE MAKE IT HAPPEN."],
    sub: "한국에서 태국까지. 가능성을 현실로.",
    dot: "CONNECT",
  },
  {
    image: "/assets/img/pages/home-hero-2-concept.jpg",
    lines: ["LESS TALK.", "MORE ACTION."],
    sub: "말보다 실행. 계획보다 현장.",
    dot: "EXECUTE",
  },
  {
    image: "/assets/img/pages/home-hero-3-concept.jpg",
    lines: ["BUSINESS", "IN MOTION."],
    sub: "시장을 움직이고, 비즈니스를 실현합니다.",
    dot: "EXPAND",
  },
];
const TOTAL = SLIDES.length;
const SLIDE_MS = 6800; // 6.8초마다 다음 장
// 실적 카드 숫자 (첫 칸 2017은 고정, 나머지는 화면에 들어오면 1.25초 동안 올라감)
const STAT_VALUES = [2017, 2009, 500, 23];
const STAT_EXTRAS = ["", "+", "+", ""];
const fmt = (n) => n.toLocaleString("en-US");
const COUNT_AFTER_RISE = 750; // 실적 카드가 떠오른 뒤 숫자를 올리기 시작(ms) — 카드 등장 0.3초 지연 + 대부분 보이는 시점
const INTRO_REVEAL = 2150; // 첫 방문 인트로가 걷히기 시작하고 히어로가 등장하는 시점(ms, public/index.html 과 같게)

// 이번 방문(새로고침 전까지)에 HOME 을 이미 봤는지. 두 번째부터는 첫 등장 연출·숫자 카운트를 생략한다.
let homeVisited = false;

// 첫 방문 인트로(public/index.html)가 걷히는 시점에 cb 실행. 인트로가 없으면 바로 실행. 취소 함수를 돌려준다.
function afterIntro(cb) {
  const R = document.documentElement;
  if (!R.classList.contains("intro-on")) {
    cb();
    return () => {};
  }
  let t = 0;
  const run = () => {
    const begun = window.__inbaIntro || performance.now();
    t = setTimeout(cb, Math.max(0, INTRO_REVEAL - (performance.now() - begun)));
  };
  if (window.__inbaIntro) run();
  else document.addEventListener("inba:intro", run, { once: true });
  return () => {
    clearTimeout(t);
    document.removeEventListener("inba:intro", run);
  };
}

export default function Home() {
  const [active, setActive] = useState(0);
  const [outgoing, setOutgoing] = useState(null);
  const activeRef = useRef(0);
  const timers = useRef({});
  const statsRef = useRef(null);
  const networkRef = useRef(null);
  const mainRef = useRef(null);
  const firstVisit = useRef(!homeVisited).current;
  useEffect(() => {
    homeVisited = true;
  }, []);

  // 구역 제목은 단어별로 올라온다
  usePageMotion(mainRef, {
    split: [".network .section-head h2", ".services .section-head h2", ".selected .work-head h2"],
  });

  // HERO 슬라이드 전환 (오른쪽에서 닦아내듯 전환). 실적 카드는 흔들지 않는다(3번).
  const go = useCallback((index) => {
    const next = (index + TOTAL) % TOTAL;
    const old = activeRef.current;
    if (next === old) return;
    const t = timers.current;
    clearTimeout(t.cleaner);
    activeRef.current = next;
    document.documentElement.classList.remove("ib-calm"); // 재방문 때 멈춰 둔 문구 등장 효과를 다시 켬
    setOutgoing(old);
    setActive(next);
    t.cleaner = setTimeout(() => setOutgoing(null), reducedMotion() ? 0 : 650);
  }, []);

  const stop = useCallback(() => {
    clearInterval(timers.current.cycle);
    timers.current.cycle = null;
  }, []);
  const start = useCallback(() => {
    stop();
    if (!reducedMotion() && !document.hidden) {
      timers.current.cycle = setInterval(() => go(activeRef.current + 1), SLIDE_MS);
    }
  }, [go, stop]);

  // 자동 전환 (탭이 숨겨지면 멈춤). 첫 방문 인트로가 있으면 걷힌 뒤부터 6.8초를 센다.
  useEffect(() => {
    const t = timers.current;
    const cancel = afterIntro(start);
    const onVisibility = () => (document.hidden ? stop() : start());
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancel();
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
      clearTimeout(t.cleaner);
    };
  }, [start, stop]);

  // 실적 숫자 카운트 — 첫 방문에만, 카드가 떠오른 뒤 시작(2번). 재방문은 최종 숫자 그대로(9번).
  useEffect(() => {
    const stats = statsRef.current;
    if (!stats) return undefined;
    const els = [...stats.querySelectorAll(".hero-stat b")];
    const setFinal = () =>
      els.forEach((el, i) => {
        el.textContent = i === 0 ? "2017" : fmt(STAT_VALUES[i]) + STAT_EXTRAS[i];
      });
    let raf = 0;
    const countUp = () => {
      if (reducedMotion()) {
        setFinal();
        return;
      }
      els[0].textContent = "2017";
      const begins = performance.now();
      const duration = 1250;
      const ease = (x) => 1 - Math.pow(1 - x, 3);
      const frame = (now) => {
        const p = Math.min(1, (now - begins) / duration);
        for (let i = 1; i < els.length; i++) els[i].textContent = fmt(Math.round(STAT_VALUES[i] * ease(p))) + STAT_EXTRAS[i];
        if (p < 1) raf = requestAnimationFrame(frame);
        else setFinal();
      };
      raf = requestAnimationFrame(frame);
    };
    setFinal();
    if (!firstVisit) return undefined;
    let wait = 0;
    let cancelIntro = () => {};
    const schedule = () => {
      cancelIntro = afterIntro(() => {
        wait = setTimeout(countUp, COUNT_AFTER_RISE);
      });
    };
    if (!("IntersectionObserver" in window)) {
      schedule();
      return () => {
        cancelIntro();
        clearTimeout(wait);
        cancelAnimationFrame(raf);
      };
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          schedule();
        }
      },
      { threshold: 0.1 }
    );
    io.observe(stats);
    return () => {
      io.disconnect();
      cancelIntro();
      clearTimeout(wait);
      cancelAnimationFrame(raf);
      setFinal();
    };
  }, [firstVisit]);

  // 스크롤 모션 (최종 마감본). 외부 라이브러리 없이 한 프레임에 묶어 계산한다. 값은 시안 문서 02 기능명세 1절.
  //  - html.ib-motion(전체) · html.ib-motion-d(761px 이상) · html.ib-intro(첫 진입)를 붙일 때만 CSS 모션이 동작
  //  - '동작 줄이기' 설정이면 아무것도 붙이지 않아 모든 요소가 처음부터 정지 상태
  //  - 첫 화면 깜빡임이 없도록 그리기 전에(useLayoutEffect) 클래스를 붙인다
  //  - 첫 방문(이번 접속에서 처음 HOME): 히어로 첫 등장(ib-intro). 인트로가 있으면 걷힐 때까지 멈춰 둠(intro-hold)
  //  - 재방문: 첫 등장 생략, 처음 보이는 문구의 등장 효과도 생략(ib-calm, 슬라이드가 바뀌면 해제)
  useLayoutEffect(() => {
    if (reducedMotion()) return undefined;
    const R = document.documentElement;
    const main = mainRef.current;
    const $$ = (s, c = main) => [...c.querySelectorAll(s)];
    const mqD = window.matchMedia("(min-width:761px)");
    R.classList.add("ib-motion");
    let cancelIntro = () => {};
    if (firstVisit) {
      R.classList.add("ib-intro");
      if (R.classList.contains("intro-on")) {
        R.classList.add("intro-hold");
        cancelIntro = afterIntro(() => R.classList.remove("intro-hold"));
      }
    } else {
      R.classList.add("ib-calm");
    }
    const setD = () => R.classList.toggle("ib-motion-d", mqD.matches);
    setD();
    $$(".network__lane").forEach((l) => $$(".partner-logo", l).forEach((p, i) => p.style.setProperty("--i", i)));
    const cards = $$(".services .service");
    cards.forEach((c, i) => c.style.setProperty("--k", i));
    const shots = $$(".selected__shot");
    shots.forEach((s, i) => s.style.setProperty("--i", i));
    // 제목(h2)은 단어별 등장(usePageMotion)이 맡고, 나머지 라벨·설명·버튼이 차례로 떠오른다
    const blocks = [
      [...$$(".network .section-head > div > *:not(h2)"), ...$$(".network .section-head > p")],
      [...$$(".services .section-head > div > *:not(h2)"), ...$$(".services .section-head > p")],
      [...$$(".selected .work-head > div > *:not(h2)"), ...$$(".selected .view-all")],
    ];
    const reveal = [];
    blocks.forEach((b) =>
      b.forEach((el, i) => {
        el.classList.add("ib-rv");
        el.style.setProperty("--d", i * 0.08 + "s");
        reveal.push(el);
      })
    );
    if (!mqD.matches) {
      cards.forEach((c) => {
        c.classList.add("ib-rv");
        reveal.push(c);
      });
    }
    // 화면 아래 14% 지점에 들어오면 등장(제목·카드는 is-in). 로고 줄은 6% 지점에서 바로 켠다(is-lit, 6번).
    const lanes = $$(".network__lane");
    const grid = main.querySelector(".selected__grid");
    const show = (el) => el.classList.add(el.classList.contains("network__lane") ? "is-lit" : "is-in");
    const targets = [...reveal, ...(grid ? [grid] : [])];
    const ios = [];
    if ("IntersectionObserver" in window) {
      const watch = (els, rootMargin) => {
        const io = new IntersectionObserver(
          (es) =>
            es.forEach((e) => {
              if (e.isIntersecting) {
                show(e.target);
                io.unobserve(e.target);
              }
            }),
          { rootMargin }
        );
        els.forEach((el) => io.observe(el));
        ios.push(io);
      };
      watch(targets, "0px 0px -14% 0px");
      watch(lanes, "0px 0px -6% 0px");
    } else {
      [...targets, ...lanes].forEach(show);
    }
    // PC: 히어로 진행도(--hp), WHAT WE DO 카드 들어온 정도(--en)·덮인 정도(--cv), SELECTED WORK 시차(--py)
    const hero = main.querySelector(".hero");
    const stage = main.querySelector("#heroStage");
    const head = document.querySelector(".ib-header");
    const clamp = (v) => Math.min(1, Math.max(0, v));
    let raf = 0;
    const tick = () => {
      raf = 0;
      if (!mqD.matches) return;
      const vh = window.innerHeight;
      if (stage && hero) {
        const r = stage.getBoundingClientRect();
        const top = head ? head.offsetHeight : 74;
        hero.style.setProperty("--hp", clamp((top - r.top) / r.height).toFixed(4));
      }
      cards.forEach((c, i) => {
        const r = c.getBoundingClientRect();
        const h = c.offsetHeight;
        const n = cards[i + 1];
        c.style.setProperty("--en", clamp((vh - r.top) / (vh * 0.6)).toFixed(4));
        c.style.setProperty("--cv", n ? clamp((r.top + h - n.getBoundingClientRect().top) / h).toFixed(4) : "0");
      });
      shots.forEach((s, i) => {
        const r = s.getBoundingClientRect();
        if (r.bottom < -120 || r.top > vh + 120) return;
        const t = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2);
        s.style.setProperty("--py", (-t * (i % 2 ? 12 : 20)).toFixed(1) + "px");
      });
    };
    const req = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };
    const onMedia = () => {
      setD();
      req();
    };
    window.addEventListener("scroll", req, { passive: true });
    window.addEventListener("resize", req);
    if (mqD.addEventListener) mqD.addEventListener("change", onMedia);
    req(); // 첫 계산은 다음 프레임에(레이아웃이 확정된 뒤)
    return () => {
      ios.forEach((io) => io.disconnect());
      cancelIntro();
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", req);
      window.removeEventListener("resize", req);
      if (mqD.removeEventListener) mqD.removeEventListener("change", onMedia);
      R.classList.remove("ib-motion", "ib-intro", "ib-motion-d", "ib-calm", "intro-hold");
    };
  }, [firstVisit]);

  const sceneClass = (i) => "hero-scene" + (i === active ? " is-active" : "") + (i === outgoing ? " is-outgoing" : "");
  const restartWith = (fn) => () => {
    fn();
    start();
  };

  return (
    <>
      <main ref={mainRef}>
        <section aria-label="INBA Thailand HERO" className="hero hero--b">
          <div className="shell hero-frame">
            <div className="hero-stage" id="heroStage" onMouseEnter={stop} onMouseLeave={start}>
              <div aria-hidden="true" className="hero-scenes">
                {SLIDES.map((s, i) => (
                  <Fragment key={s.image}>
                    {i > 0 && " "}
                    <img alt="" className={sceneClass(i)} decoding="async" loading="eager" src={s.image} />
                  </Fragment>
                ))}
              </div>
              <div className="hero-stage__content">
                <div className="micro">{"INBA THAILAND "}<span className="micro-slash">/</span>{" SINCE 2017"}</div>
                <div aria-live="polite" className="hero-messages">
                  {SLIDES.map((s, i) => (
                    <div key={s.image} aria-hidden={i === active ? "false" : "true"} className={"hero-message" + (i === active ? " is-active" : "")}>
                      <h1><span>{s.lines[0]}</span><span>{s.lines[1]}</span></h1>
                      <p>{s.sub}</p>
                    </div>
                  ))}
                </div>
                <div aria-label="HERO 슬라이드 컨트롤" className="hero-controls">
                  <span id="slideCounter">{`${String(active + 1).padStart(2, "0")} / 03`}</span>
                  {" "}
                  <div className="hero-dots">
                    {SLIDES.map((s, i) => (
                      <button
                        key={s.dot}
                        aria-label={`${s.dot} 이미지 보기`}
                        className={i === active ? "is-active" : undefined}
                        aria-current={i === active ? "true" : "false"}
                        data-slide={i}
                        onClick={restartWith(() => go(i))}
                      />
                    ))}
                  </div>
                  {" "}
                  <button aria-label="이전 슬라이드" className="hero-arrow" id="prevHero" onClick={restartWith(() => go(activeRef.current - 1))}>‹</button>
                  <button aria-label="다음 슬라이드" className="hero-arrow" id="nextHero" onClick={restartWith(() => go(activeRef.current + 1))}>›</button>
                </div>
              </div>
              <a className="hero-next" href="#network" aria-label="다음 섹션 OUR NETWORK로 이동">
                {"SCROLL TO EXPLORE "}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                  <path d="M12 4v15m-7-7 7 7 7-7" />
                </svg>
              </a>
            </div>
            <div ref={statsRef} aria-label="INBA 주요 실적" className="hero-stats">
              <div className="hero-stats__inner">
                <div className="hero-stat"><b>2017</b><span>ESTABLISHED</span></div>
                <div className="hero-stat"><b>2,009+</b><span>BUSINESS MATCHINGS</span></div>
                <div className="hero-stat"><b>500+</b><span>FDA REGISTRATIONS</span></div>
                <div className="hero-stat"><b>23</b><span>EVENTS DELIVERED</span></div>
              </div>
            </div>
          </div>
        </section>
        <section ref={networkRef} className="network" id="network">
          <div className="shell">
            <div className="section-head">
              <div><div className="eyebrow">{"HOME-02 / OUR NETWORK & EXPERIENCE"}</div><h2>다양한 파트너와,<br />현장에서 만들어온 경험.</h2></div>
              <p>공공기관 프로젝트부터 현지 유통, 한국 브랜드의 태국 시장 진출까지. 다양한 협업과 수행 경험이 INBA의 실행력을 보여줍니다.</p>
            </div>
            <div className="network__lanes">
              <div className="network__lane">
                <div className="network__category">
                  <span className="index">01</span>
                  <strong>{"PUBLIC SECTOR &"}<br />INSTITUTIONS</strong>
                </div>
                <div className="network__names">
                  <div className="partner-logo" data-company="aT">
                    <img alt="aT 공식 로고" loading="lazy" src="/assets/img/logos/at.png" />
                  </div>
                  <div className="partner-logo" data-company="KINTEX">
                    <img alt="KINTEX 공식 로고" loading="lazy" src="/assets/img/logos/kintex.png" />
                  </div>
                  <div className="partner-logo" data-company="홍천군">
                    <img alt="홍천군 공식 로고" loading="lazy" src="/assets/img/logos/hongcheon.png" />
                  </div>
                  <div className="partner-logo" data-company="강원경제진흥원">
                    <img alt="강원경제진흥원 공식 로고" loading="lazy" src="/assets/img/logos/gwep.png" />
                  </div>
                </div>
              </div>
              <div className="network__lane">
                <div className="network__category">
                  <span className="index">02</span>
                  <strong>{"DISTRIBUTION &"}<br />VENUES</strong>
                </div>
                <div className="network__names">
                  <div className="partner-logo" data-company="CP ALL">
                    <img alt="CP ALL 공식 로고" loading="lazy" src="/assets/img/logos/cpall.png" />
                  </div>
                  <div className="partner-logo" data-company="Robinson">
                    <img alt="Robinson 공식 로고" loading="lazy" src="/assets/img/logos/robinson.png" />
                  </div>
                  <div className="partner-logo" data-company="Paradise Park">
                    <img alt="Paradise Park 공식 로고" loading="lazy" src="/assets/img/logos/paradisepark.svg" />
                  </div>
                  <div className="partner-logo" data-company="Seacon Square">
                    <img alt="Seacon Square 공식 로고" loading="lazy" src="/assets/img/logos/seacon.png" />
                  </div>
                </div>
              </div>
              <div className="network__lane">
                <div className="network__category">
                  <span className="index">03</span>
                  <strong>{"KOREAN BRANDS &"}<br />PROJECTS</strong>
                </div>
                <div className="network__names">
                  <div className="partner-logo" data-company="모두바른">
                    <img alt="모두바른 공식 로고" loading="lazy" src="/assets/img/logos/modoobareun.png" />
                  </div>
                  <div className="partner-logo" data-company="Dr.MISIMOU">
                    <img alt="Dr.MISIMOU 공식 로고" loading="lazy" src="/assets/img/logos/drmisimou.png" />
                  </div>
                  <div className="partner-logo" data-company="DIMODIVE">
                    <img alt="DIMODIVE 공식 로고" loading="lazy" src="/assets/img/logos/dimodive.png" />
                  </div>
                  <div className="partner-logo" data-company="GLUCA">
                    <img alt="GLUCA 공식 로고" loading="lazy" src="/assets/img/logos/gluca.png" />
                  </div>
                </div>
              </div>
            </div>
            <p className="network__foot">* 로고는 수행·협업 유형을 구분해 표기한 것입니다.</p>
          </div>
        </section>
        <section className="services" id="services">
          <div className="shell">
            <div className="section-head">
              <div><div className="eyebrow">HOME-03 / WHAT WE DO</div><h2>시장을 열고, 고객과 연결하고,<br />현장에서 실행한다.</h2></div>
              <p>시장 진출부터 고객 접점, 현장 사업 운영까지. INBA Thailand의 세 가지 핵심 사업을 소개합니다.</p>
            </div>
            <article className="service service--trade">
              <div className="service__media">
                <img alt="수입·유통 분야 이미지" loading="eager" src="/assets/img/pages/home-trade-containers.jpg" />
              </div>
              <div className="service__body">
                <span className="service__label">01 / TRADE · 수입·인허가·유통</span>
                <h3 className="service__title">시장을 열고</h3>
                <p className="service__desc">
                  태국 시장과 제품의 진출 가능성을 검토하고, FDA 등 인허가 준비와 수입·통관, 현지 바이어 발굴을 지원합니다. 유통채널 진입부터 실제 판매 운영까지 사업의 흐름을 연결합니다.
                </p>
                <div className="service__selected">
                  <b>SELECTED WORK</b>
                  <span>CP ALL × GLUCA 브랜드 런칭</span>
                  <span className="dash">/</span>
                  <span>Robinson 백화점 유통·납품</span>
                </div>
                <span className="service__artnum">01</span>
              </div>
              <a aria-label="TRADE 사업 자세히 보기" className="service__arrow" href="/business#trade">↗</a>
            </article>
            <article className="service service--digital">
              <div className="service__body">
                <span className="service__label">02 / DIGITAL · 디지털 마케팅·커머스</span>
                <h3 className="service__title">고객과 연결하고</h3>
                <p className="service__desc">
                  태국 소비자의 관심과 구매 행동에 맞춘 SNS 콘텐츠와 캠페인을 기획합니다. 현지 인플루언서 협업, 숏폼, 라이브커머스를 통해 브랜드 홍보부터 판매 연계까지 실행합니다.
                </p>
                <div className="service__selected">
                  <b>SELECTED WORK</b>
                  <span>aT 태국·미얀마 뉴미디어 마케팅</span>
                  <span className="dash">/</span>
                  <span>한·태 인플루언서 라이브커머스</span>
                </div>
                <span className="service__artnum">02</span>
              </div>
              <div className="service__media">
                <img style={{ objectPosition: "50% 50%" }} alt="INBA 라이브커머스 스튜디오 촬영 현장" loading="eager" src="/assets/img/photos/home-digital.jpg" />
              </div>
              <a aria-label="DIGITAL 사업 자세히 보기" className="service__arrow" href="/business#digital">↗</a>
            </article>
            <article className="service service--events">
              <div className="service__media">
                <img style={{ objectPosition: "50% 60%" }} alt="Pet Fair SE Asia 2026 한국관 현장" loading="eager" src="/assets/img/photos/pf-18-g2.jpg" />
              </div>
              <div className="service__body">
                <span className="service__label">03 / EVENTS · 전시·행사·수출상담</span>
                <h3 className="service__title">현장에서 실행한다</h3>
                <p className="service__desc">
                  박람회·팝업스토어·수출상담회를 기획하고 참가기업과 바이어의 만남을 지원합니다. 현지 협력사 관리, 통역, 현장 진행과 결과 정리까지 사업 목적에 맞춰 운영합니다.
                </p>
                <div className="service__selected">
                  <b>SELECTED WORK</b>
                  <span>Paradise Park 홍천 특산품 행사</span>
                  <span className="dash">/</span>
                  <span>Seacon Square KOREA DAY</span>
                </div>
                <span className="service__artnum">03</span>
              </div>
              <a aria-label="EVENTS 사업 자세히 보기" className="service__arrow" href="/business#events">↗</a>
            </article>
          </div>
        </section>
        <section className="selected" id="selected">
          <div className="shell">
            <div className="work-head">
              <div>
                <div className="eyebrow">SELECTED WORK / FIELD NOTES</div>
                <h2>SELECTED WORK<span className="blue">.</span></h2>
                <p>실제 수행 프로젝트의 현장 기록.<br />INBA의 경험을 확인해 보세요.</p>
              </div>
              <a className="view-all" href="/portfolio">VIEW MORE ↗</a>
            </div>
            <div className="selected__grid">
              <a className="selected__shot" href="/portfolio?project=18">
                <img alt="Pet Fair SE Asia 2026 한국관 라이브 방송" loading="lazy" src="/assets/img/photos/home-work-1.jpg" style={{ objectPosition: "50% 45%" }} />
                <span className="shot-no">01</span>
                <span className="shot-name">PET FAIR SE ASIA 2026</span>
              </a>
              {" "}
              <a className="selected__shot" href="/portfolio?project=5">
                <img alt="대구국제뷰티엑스포 태국관 부스" loading="lazy" src="/assets/img/photos/home-work-2.jpg" style={{ objectPosition: "50% 55%" }} />
                <span className="shot-no">02</span>
                <span className="shot-name">DAEGU BEAUTY EXPO</span>
              </a>
              {" "}
              <a className="selected__shot" href="/portfolio?project=6">
                <img alt="Paradise Park 홍천 특산품 판매전" loading="lazy" src="/assets/img/photos/home-work-3.jpg" style={{ objectPosition: "50% 45%" }} />
                <span className="shot-no">03</span>
                <span className="shot-name">PARADISE PARK</span>
              </a>
              {" "}
              <a className="selected__shot" href="/portfolio?project=3">
                <img alt="한국유학박람회 aT 체험존" loading="lazy" src="/assets/img/photos/home-work-4.jpg" style={{ objectPosition: "50% 45%" }} />
                <span className="shot-no">04</span>
                <span className="shot-name">aT K-FOOD NEW MEDIA</span>
              </a>
              {" "}
              <a className="selected__shot" href="/portfolio?project=14">
                <img alt="2018 K-Beauty Expo 태국 시장 설명회" loading="lazy" src="/assets/img/photos/home-work-5.jpg" style={{ objectPosition: "50% 50%" }} />
                <span className="shot-no">05</span>
                <span className="shot-name">K-BEAUTY EXPO SEMINAR</span>
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
